import Box from "@mui/material/Box";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import styled from "@emotion/styled";

import "./../genericModels.css";

import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { setSnackbar } from "../../../../util/state/globalAction";
import CustomLoader from "../../../../util/component/CustomLoader";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from "@mui/material/index";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { equipmentActions } from "./service/equipment.actions";
import GenericFilterBox from "../common/GenericFilterBox";
import {
  ColDef,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { nameof } from "../common/ObjectUtils";
import EquipmentTypes from "./Types";
import { customTheme } from "../../../../config/theme/MuiTheme";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import FileSaver from "file-saver";
import DeleteConfirmation from "./dialogs/DeleteConfirmation";
import { ContextOperation } from "../../context/OperationContext";
import FormInput from "../common/FormInput";
import { Parser, onChangeTextNumber } from "../../utils";
import { useHasScope } from "../../../../config/globalConfig";
import AgGridOperationTable from "../common/AgGridOperationTable";

const StyledActionsBox = styled(Mui.Box)({
  backgroundColor: customTheme.palette.neutral.bright,
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
});

export const EquipmentTab = () => {
  const hasScope = useHasScope;
  const { tenant } = useContext(ContextOperation);
  const { dispatchGlobalData } = useContext(GlobalContext);
  const { t } = useTranslation(["operation"]);
  const equipment = equipmentActions();
  const [priceErrorMsg, setPriceErrorMsg] = useState("");
  const [isPriceError, setIsPriceError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [confirmSave, setConfirmSave] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState<boolean>(false);
  const [selectedEquipment, setSelectedEquipment] = useState<
    EquipmentTypes.IEquipment | undefined
  >(undefined);

  const [resetForm, setResetForm] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(1);
  const pageSize = 10;
  const [remoteErrorMsg, setRemoteErrorMsg] = useState("");
  const [remoteError, setRemoteError] = useState<boolean>(false);

  const [filters, setFilters] = useState<EquipmentTypes.IEquipmentFilters>({});

  const [formInputs, setFormInputs] =
    useState<EquipmentTypes.IEquipmentFilters>({});

  useEffect(() => {
    setRemoteError(false);
    setRemoteErrorMsg("");
  }, [createModalOpen]);

  const allValidations = [
    {
      value: true,
      label: t("internOptionSelectName.valid"),
    },
    {
      value: false,
      label: t("internOptionSelectName.inValid"),
    },
  ];

  const validationSchema = yup.object({
    code: yup.string().trim(),
    description: yup.string().trim(),
    valid: yup.string().trim(),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<EquipmentTypes.IEquipment>({
    resolver: yupResolver(validationSchema),
  });

  const onSuccess = (action: string) => {
    setLoading(false);
    onCloseCreateModal();
    setResetForm(!resetForm);
    dispatchGlobalData({
      type: setSnackbar,
      payload: {
        snackbar: {
          message: t(`vehicleEquipment.modifyEquipment.actionSuccess${action}`),
          type: snackbarTypes.success,
          show: true,
          autoClose: true,
        },
      },
    });
  };

  const onSubmit = (params: EquipmentTypes.IEquipment) => {
    if (getValues().id && !getValues().valid)  {
      if (confirmSave === false) {
        setConfirmSave(true);
        return;
      } else {
        setConfirmSave(false);
      }
    }

    if (Object.keys(errors).length === 0 && !remoteError) {
      setLoading(true);
      params.price = params.price;
      let values = getValues();
      values.id
        ? equipment
            .editEquipment(tenant, values)
            .then((response: any) => {
              onSuccess("Edit");
            })
            .catch((error: any) => {
              setLoading(false);
              dispatchGlobalData({
                type: setSnackbar,
                payload: {
                  snackbar: {
                    message: t("vehicleEquipment.modifyEquipment.actionError"),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                  },
                },
              });
            })
        : equipment
            .createEquipment(tenant, params)
            .then((response: any) => {
              onSuccess("Create");
            })
            .catch((error: any) => {
              setLoading(false);
              dispatchGlobalData({
                type: setSnackbar,
                payload: {
                  snackbar: {
                    message: t("vehicleEquipment.modifyEquipment.actionError"),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                  },
                },
              });
            });
    }
  };

  const handleDelete = (id: number) => {
    setLoading(true);
    equipment
      .deleteEquipment(tenant, id)
      .then((response: any) => {
        onSuccess("Delete");
      })
      .catch((error: any) => {
        setLoading(false);
        dispatchGlobalData({
          type: setSnackbar,
          payload: {
            snackbar: {
              message: t("vehicleEquipment.modifyEquipment.actionError"),
              type: snackbarTypes.error,
              show: true,
              autoClose: true,
            },
          },
        });
      });
  };

  const handleDownloadTemplate = () => {
    setLoading(true);
    equipment
      .downloadTemplate()
      .then((response: any) => {
        const filename =
          response.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        dispatchGlobalData({
          type: setSnackbar,
          payload: {
            snackbar: {
              message: t("vehicleEquipment.modifyEquipment.actionError"),
              type: snackbarTypes.error,
              show: true,
              autoClose: true,
            },
          },
        });
      });
  };

  const handleDownloadExcel = () => {
    setLoading(true);
    equipment
      .downloadExcel(tenant, formInputs)
      .then((response: any) => {
        const filename =
          response.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        dispatchGlobalData({
          type: setSnackbar,
          payload: {
            snackbar: {
              message: t("vehicleEquipment.modifyEquipment.actionError"),
              type: snackbarTypes.error,
              show: true,
              autoClose: true,
            },
          },
        });
      });
  };

  const handleUploadEquipment = (event: any) => {
    if (event.target.files && event.target.files[0]) {
      let currentFile = event.target.files[0];
      setLoading(true);
      equipment
        .uploadEquipment(tenant, currentFile)
        .then((response: any) => {
          setLoading(false);
          setResetForm(!resetForm);
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message: t("vehicleEquipment.modifyEquipment.successUpload"),
                type: snackbarTypes.success,
                show: true,
                autoClose: true,
              },
            },
          });
        })
        .catch((error: any) => {
          setLoading(false);
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message:
                  error.status === 400
                    ? t("vehicleEquipment.modifyEquipment.errorUploadFile")
                    : t("vehicleEquipment.modifyEquipment.errorUpload"),
                type: snackbarTypes.error,
                show: true,
                autoClose: true,
              },
            },
          });
        });
      event.target.value = null;
    }
  };

  const handleChange = (value: any, type: any) => {
    setFilters({
      ...filters,
      [type]: value,
    });
  };

  const resetFilters = () => {
    setFilters({});
    setFormInputs({});
  };

  const searchFilters = () => {
    setFormInputs(filters);
    setResetForm(!resetForm);
  };

  const equipmentDataSource: IServerSideDatasource = useMemo(
    () => ({
      getRows: (params: IServerSideGetRowsParams) => {
        if (formInputs) {
          const startRow = params.request.startRow
            ? params.request.startRow
            : 0;
          const endRow = params.request.endRow;
          let pageSize = 10;
          if (
            endRow !== null &&
            endRow !== undefined &&
            startRow !== null &&
            startRow !== undefined
          ) {
            pageSize = endRow - startRow;
          }
          const pageNumber = startRow / pageSize;

          formInputs.code = formInputs.code?.trim();
          formInputs.description = formInputs.description?.trim();

          equipment
            .getAll(tenant, {
              pageNumber: pageNumber,
              pageSize,
              ...formInputs,
            })
            .then((response: any) => {
              params.success({
                rowData: response.data.content,
                rowCount: response.data.totalElements,
              });
              setTotalElements(response.data.totalElements);
            });
        }
      },
    }),
    [formInputs, resetForm, tenant]
  );

  const equipmentColumns: ColDef[] = [
    {
      field: nameof<EquipmentTypes.IEquipment>("code"),
      headerName: t("vehicleEquipment.fields.code"),
      flex: 1,
    },
    {
      field: nameof<EquipmentTypes.IEquipment>("descr"),
      headerName: t("vehicleEquipment.fields.description"),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<EquipmentTypes.IAllEquipments>
      ) => {
        return (
          <Mui.Tooltip title={props.value}>
            <div
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {props.value}
            </div>
          </Mui.Tooltip>
        );
      },
    },
    {
      field: nameof<EquipmentTypes.IEquipment>("notes"),
      headerName: t("vehicleEquipment.fields.note"),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<EquipmentTypes.IAllEquipments>
      ) => {
        return (
          <Mui.Tooltip title={props.value}>
            <div
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {props.value}
            </div>
          </Mui.Tooltip>
        );
      },
    },
    {
      field: nameof<EquipmentTypes.IEquipment>("valid"),
      headerName: t("vehicleEquipment.fields.validity"),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<EquipmentTypes.IAllEquipments>
      ) => {
        return (
          <Mui.Chip
            label={
              props.value
                ? t("internOptionSelectName.valid")
                : t("internOptionSelectName.inValid")
            }
            color={props.value ? "success" : "error"}
            variant="filled"
          />
        );
      },
    },
    {
      field: nameof<EquipmentTypes.IEquipment>("price"),
      headerName: t("vehicleEquipment.fields.price"),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<EquipmentTypes.IAllEquipments>
      ) => {
        return <span>€ {Parser.price(props.value)}</span>;
      },
    },
    {
      field: "",
      maxWidth: 80,
      cellStyle: {
        padding: "0px",
        border: "none",
      },
      cellRenderer: (props: ICellRendererParams<any>) => {
        return (
          <StyledActionsBox>
            {hasScope("SCOPE_UPDATE_MDVEHICLE_EQUIPMENT") && (
              <Mui.IconButton
                color="accent"
                onClick={() => {
                  setValue("id", props.data.id);
                  setValue("code", props.data.code);
                  setValue("descr", props.data.descr);
                  setValue("valid", props.data.valid);
                  setValue("price", props.data.price);
                  setValue("notes", props.data.notes);
                  setCreateModalOpen(true);
                }}
              >
                <Mui.Tooltip title={t("tooltipTitle.edit")}>
                  <MuiIcons.Edit />
                </Mui.Tooltip>
              </Mui.IconButton>
            )}

            {hasScope("SCOPE_DELETE_MDVEHICLE_EQUIPMENT") && (
              <Mui.IconButton
                color="accent"
                onClick={() => {
                  setSelectedEquipment(props.data);
                  setDeleteConfirmationVisible(true);
                }}
              >
                <Mui.Tooltip title={t("tooltipTitle.delete")}>
                  <MuiIcons.Delete />
                </Mui.Tooltip>
              </Mui.IconButton>
            )}
          </StyledActionsBox>
        );
      },
    },
  ];

  const onChangeCode = (code: string) => {
    equipment
      .checkCode(tenant, code)
      .then((response: any) => {
        setRemoteErrorMsg(t("vehicleEquipment.modifyEquipment.codeExists"));
        setRemoteError(true);
      })
      .catch((error: any) => {
        setRemoteErrorMsg("");
        setRemoteError(false);
      });
  };

  const onInvalidValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof EquipmentTypes.IEquipment,
    message: string
  ) => {
    const val = watch(field);
    if (val === undefined || val === null || val === "") {
      event.currentTarget.setCustomValidity(message);
    } else {
      event.currentTarget.setCustomValidity("");
    }
  };

  const priceError = (value: any) => {
    const reg = new RegExp("^[0-9]*([,.]*[0-9]*)?$");
    if (!reg.test(value)) {
      setPriceErrorMsg(t("vehicleEquipment.fields.onlyNumber"));
      setIsPriceError(true);
    } else {
      setPriceErrorMsg("");
      setIsPriceError(false);
    }
  };

  const onCloseCreateModal = () => {
    setCreateModalOpen(false);
    reset();
    setValue("code", "");
    setValue("descr", "");
    setValue("valid", "");
    setValue("price", "");
    setValue("notes", "");
    setConfirmSave(false);
  };

  const onChangeNumber = (
    name: string,
    value: string,
    decimalPointLength?: number,
    numberLength?: number,
    maxNumber?: number,
    minNumber?: number
  ) => {
    const newVal = onChangeTextNumber(value, {
      decimalPointLength,
      numberLength,
      maxNumber,
      minNumber,
    });
    if (newVal !== undefined) {
      console.log(+newVal, "+newVal");
      console.log(newVal, "+newVal value");
      setValue("price", newVal);
    }
  };

  console.log(watch(), "watch valeu");

  return (
    <>
      <Box className="equipment">
        {loading && <CustomLoader loading={loading} />}
        
        <form id="equipment-tab-search-form" onSubmit={(e) => {e.preventDefault(); searchFilters();}}>
          <GenericFilterBox
            formId="equipment-tab-search-form"
            showExtrasFilters={true}
            onReset={resetFilters}
          >
            <Mui.TextField
              label={t("vehicleEquipment.fields.code")}
              fullWidth
              value={filters.code || ""}
              onChange={(event) => handleChange(event.target.value, "code")}
            />

            <Mui.TextField
              label={t("vehicleEquipment.fields.description")}
              fullWidth
              value={filters.description || ""}
              onChange={(event) =>
                handleChange(event.target.value, "description")
              }
            />

            <FormInput
              name="vehicleEquipment.fields.validity"
              type="select"
              options={allValidations}
              label={t("vehicleEquipment.fields.validity")}
              value={filters?.valid !== undefined ? filters.valid : ""}
              onChange={(val) => handleChange(val, "valid")}
            />

            <Mui.TextField
              label={t("vehicleEquipment.fields.note")}
              fullWidth
              value={filters.notes || ""}
              onChange={(event) => handleChange(event.target.value, "notes")}
            />

            <Mui.TextField
              label={t("vehicleEquipment.fields.minPrice")}
              value={filters.minPrice || ""}
              onChange={(event) => {
                let valTmp = event.target.value.replace(/\,/g, ".");
                const reg = new RegExp("^[0-9]*([,.][0-9]*)?$");
                if (reg.test(valTmp)) {
                  handleChange(valTmp, "minPrice");
                }
              }}
              fullWidth
            />

            <Mui.TextField
              label={t("vehicleEquipment.fields.maxPrice")}
              value={filters.maxPrice || ""}
              onChange={(event) => {
                let valTmp = event.target.value.replace(/\,/g, ".");
                const reg = new RegExp("^[0-9]*([,.][0-9]*)?$");
                if (reg.test(valTmp)) {
                  handleChange(valTmp, "maxPrice");
                }
              }}
              fullWidth
            />
          </GenericFilterBox>
        </form>

        <Box
          className="carGenericOuterFilterBox"
          sx={{ backgroundColor: "white" }}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            alignContent="center"
            direction="row"
          >
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  justifyContent: "flex-end",
                  display: "flex",
                  gap: "10px",
                }}
              >
                {(hasScope("SCOPE_CREATE_MDVEHICLE_EQUIPMENT") ||
                  hasScope("SCOPE_UPDATE_MDVEHICLE_EQUIPMENT")) && (
                  <>
                    <Button
                      startIcon={<FileDownloadIcon />}
                      className="filtersBtn uploadDownloadBtns"
                      variant="outlined"
                      onClick={handleDownloadTemplate}
                    >
                      {t("vehicleEquipment.modifyEquipment.downloadTemplate")}
                    </Button>
                    <Box className="App">
                      <label htmlFor="upload-equipment">
                        <input
                          style={{ display: "none" }}
                          id="upload-equipment"
                          onChange={handleUploadEquipment}
                          name="upload-equipment"
                          type="file"
                        />
                        <Button
                          aria-label="toggle filter"
                          startIcon={<FileUploadIcon />}
                          component="span"
                          variant="contained"
                          className="filtersBtn uploadDownloadBtns"
                        >
                          {t(
                            "vehicleEquipment.modifyEquipment.uploadEquipment"
                          )}
                        </Button>
                      </label>
                    </Box>
                  </>
                )}

                <IconButton onClick={handleDownloadExcel} color="accent">
                  <Mui.Tooltip title={t("tooltipTitle.downloadExcel")}>
                    <FileDownloadIcon />
                  </Mui.Tooltip>
                </IconButton>

                {hasScope("SCOPE_CREATE_MDVEHICLE_EQUIPMENT") && (
                  <IconButton
                    onClick={() => setCreateModalOpen(true)}
                    color="accent"
                  >
                    <Mui.Tooltip title={t("tooltipTitle.add")}>
                      <AddIcon />
                    </Mui.Tooltip>
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
          <Box>
            <Box pb={2}>
              <Mui.Box>              
                <AgGridOperationTable
                  columnDefs={equipmentColumns}
                  serverSideDatasource={equipmentDataSource}
                  serverSideInfiniteScroll={true}
                  cacheBlockSize={pageSize}
                  rowModelType={"serverSide"}
                  totalElements={totalElements}
                  emptyState={{
                    title:t("vehicleEquipment.pageTitles.noEquipmentTitle"),
                    content:t("vehicleEquipment.pageTitles.noEquipmentText")
                  }}
                />              
              </Mui.Box>
            </Box>
          </Box>
        </Box>
        {loading && <CustomLoader loading={loading} />}
      </Box>

      <Dialog
        open={createModalOpen}
        fullWidth
        maxWidth="lg"
        onClose={onCloseCreateModal}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {t("vehicleEquipment.modifyEquipment.create", {
              action: getValues().id ? "Edit" : "Create",
            })}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} alignItems={"end"}>
              <Grid item sm>
                <TextField
                  fullWidth
                  id={"code"}
                  type={"text"}
                  label={t("vehicleEquipment.fields.code")}
                  {...register("code")}
                  error={Boolean(errors.code) || remoteError}
                  helperText={errors.code?.message || remoteErrorMsg}
                  required
                  inputProps={{
                    onInput: (event) => {
                      onChangeCode(event.currentTarget.value);
                    },
                  }}
                />
              </Grid>
              <Grid item sm>
                <Mui.FormControl variant="standard" fullWidth>
                  <Mui.InputLabel>
                    {t("vehicleEquipment.fields.validity")}
                  </Mui.InputLabel>
                  <Mui.Select
                    label={t("vehicleEquipment.fields.valid")}
                    fullWidth
                    id={"valid"}
                    variant={"standard"}
                    {...register("valid")}
                    error={Boolean(errors.valid)}
                    required
                    defaultValue={watch("valid")}
                  >
                    {allValidations.map((el: any, index: number) => (
                      <MenuItem key={index} value={el.value}>
                        {el.label}
                      </MenuItem>
                    ))}
                  </Mui.Select>
                </Mui.FormControl>
              </Grid>
              <Grid item sm>
                <Controller
                  control={control}
                  name="price"
                  render={({ field: { onChange, value } }: any) => (
                    <TextField
                      fullWidth
                      id={"price"}
                      required
                      label={t("vehicleEquipment.fields.price")}
                      error={Boolean(errors.price) || isPriceError}
                      helperText={errors.price?.message || priceErrorMsg}
                      onChange={(event) => {
                        priceError(event.target.value);
                        onChangeNumber("price", event.target.value, 2);
                      }}
                      value={watch("price")}
                    />
                  )}
                />
              </Grid>
              <Grid item sm>
                <TextField
                  fullWidth
                  id={"descr"}
                  type={"text"}
                  required
                  label={t("vehicleEquipment.fields.description")}
                  {...register("descr")}
                  error={Boolean(errors.descr)}
                  helperText={errors.descr?.message}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems={"end"}>
              <Grid item sm>
                <TextField
                  fullWidth
                  id={"notes"}
                  type={"text"}
                  label={t("vehicleEquipment.fields.note")}
                  {...register("notes")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Stack direction={"row"} spacing={2} width={confirmSave ? "100%" : "inherit"} justifyContent={confirmSave ? "space-between" : "inherit"}>
              {getValues().id && confirmSave && (
                <Stack direction={"row"} alignItems={"center"} style={{ opacity: "64%" }} spacing={2}>
                  <MuiIcons.ReportProblem />

                  <Typography variant="bodySmall">
                    <b>{t("vehicleEquipment.modifyEquipment.warningConfirm.warning")}</b>
                    {t("vehicleEquipment.modifyEquipment.warningConfirm.text")}
                  </Typography>
                </Stack>
              )}

              <Stack direction={"row"} spacing={2}>
                <Button
                  sx={{ height: "48px" }}
                  type="button"
                  variant="outlined"
                  onClick={onCloseCreateModal}
                >
                  {t("vehicleEquipment.kitTab.createEquipmentKit.actions.cancel")}
                </Button>
                <Button
                  type={"submit"}
                  variant={"contained"}
                  sx={{ height: "48px" }}
                >
                  {t("vehicleEquipment.kitTab.createEquipmentKit.actions.save")}
                </Button>
              </Stack>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>

      <DeleteConfirmation
        tenant={tenant}
        isOpen={deleteConfirmationVisible}
        onClose={() => setDeleteConfirmationVisible(false)}
        equipment={selectedEquipment}
        onUpdateDatasource={() => setResetForm(!resetForm)}
      />
    </>
  );
};
